import { useEffect } from 'react';

/**
 * A component that actively monitors and fixes body scroll locking issues.
 * Radix UI's Dialog/Sheet can set body/html overflow: hidden when modals open,
 * and sometimes this isn't properly cleaned up. This component uses a MutationObserver
 * to detect and reverse orphaned scroll locks when no modal is actually open.
 * 
 * Enhanced to detect locks via computed styles, classes, and attributes - not just inline styles.
 */
export function BodyScrollGuard() {
  useEffect(() => {
    // Check if any Radix modal/dialog is actually open in the DOM
    const isModalOpen = (): boolean => {
      // Check for open Radix dialogs, sheets, and overlays
      // Note: Do NOT include [data-radix-popper-content-wrapper] - these persist in Chrome's DOM
      // even after dialogs close, causing false-positive "modal open" detection
      const openRadixContent = document.querySelectorAll(
        '[data-state="open"][data-radix-dialog-content], ' +
          '[data-state="open"][data-radix-alert-dialog-content], ' +
          '[data-state="open"][role="dialog"], ' +
          '[data-state="open"][role="alertdialog"], ' +
          '[data-radix-dialog-overlay][data-state="open"], ' +
          '[data-radix-alert-dialog-overlay][data-state="open"]'
      );

      return openRadixContent.length > 0;
    };

    // Portal layouts intentionally lock body/html scrolling and manage scroll within their own container.
    // In those views (Admin/Attorney portals), treating overflow:hidden as an "orphaned lock" creates a
    // MutationObserver feedback loop that can freeze the UI and block clicks.
    const isPortalLayout = (): boolean => {
      return document.querySelector('.portal-scroll-container') !== null;
    };

    // Check if scroll is locked using multiple detection methods
    const isScrollLocked = (): boolean => {
      const body = document.body;
      const html = document.documentElement;

      // Check inline styles
      const inlineLocked =
        body.style.overflow === 'hidden' ||
        body.style.position === 'fixed' ||
        html.style.overflow === 'hidden';

      // Check computed styles (catches CSS class-based locks)
      // NOTE: Portal layouts intentionally set computed overflow hidden via CSS (:has(.portal-scroll-container)).
      // We ignore computed locks in that case.
      const bodyComputed = getComputedStyle(body);
      const htmlComputed = getComputedStyle(html);
      const computedLocked = !isPortalLayout()
        ? bodyComputed.overflow === 'hidden' ||
          bodyComputed.overflowY === 'hidden' ||
          htmlComputed.overflow === 'hidden' ||
          htmlComputed.overflowY === 'hidden' ||
          bodyComputed.position === 'fixed'
        : false;

      // Check for common lock attributes
      const attrLocked = body.hasAttribute('data-scroll-locked') || html.hasAttribute('data-scroll-locked');

      // Check for common lock classes
      const classLocked =
        body.classList.contains('overflow-hidden') ||
        html.classList.contains('overflow-hidden') ||
        body.classList.contains('scroll-locked');

      return inlineLocked || computedLocked || attrLocked || classLocked;
    };

    // Restore scroll if it's locked but no modal is open
    const restoreScrollIfNeeded = () => {
      // Portal views manage scrolling internally; never try to "restore" body/html scroll there.
      if (isPortalLayout()) return;

      if (isScrollLocked() && !isModalOpen()) {
        const body = document.body;
        const html = document.documentElement;

        // Clear inline styles
        body.style.overflow = '';
        body.style.position = '';
        body.style.top = '';
        body.style.left = '';
        body.style.right = '';
        body.style.width = '';
        body.style.paddingRight = '';
        html.style.overflow = '';

        // Remove lock attributes
        body.removeAttribute('data-scroll-locked');
        html.removeAttribute('data-scroll-locked');

        // Remove lock classes
        body.classList.remove('overflow-hidden', 'scroll-locked');
        html.classList.remove('overflow-hidden', 'scroll-locked');

        if (import.meta.env.DEV) {
          console.log('[BodyScrollGuard] Restored scroll - orphaned lock detected');
        }
      }
    };

    // Create a MutationObserver to detect style/class/attribute changes on body and html
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === 'style' || 
            mutation.attributeName === 'class' || 
            mutation.attributeName === 'data-scroll-locked') {
          // Use microtask for faster response than setTimeout
          queueMicrotask(restoreScrollIfNeeded);
          break;
        }
      }
    });

    // Observe both body and html elements
    observer.observe(document.body, { 
      attributes: true, 
      attributeFilter: ['style', 'class', 'data-scroll-locked'] 
    });
    observer.observe(document.documentElement, { 
      attributes: true, 
      attributeFilter: ['style', 'class', 'data-scroll-locked'] 
    });

    // Also check immediately on mount
    restoreScrollIfNeeded();

    // Periodic check as a safety net (every 1 second for faster recovery)
    const intervalId = setInterval(restoreScrollIfNeeded, 1000);

    return () => {
      observer.disconnect();
      clearInterval(intervalId);
    };
  }, []);

  return null;
}
